import React from 'react';
import { Shield, Heart, Award, Users, Clock, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Trust & Quality',
      description: 'We ensure all our puppies are healthy, vaccinated, and come with breed guarantees'
    },
    {
      icon: Heart,
      title: 'Care & Compassion',
      description: 'Every puppy receives love, proper nutrition, and veterinary care from day one'
    },
    {
      icon: Award,
      title: 'Expertise',
      description: 'Years of experience in breeding and pet care with professional guidance'
    },
    {
      icon: Users,
      title: 'Customer Satisfaction',
      description: '500+ happy families trust us for their pet needs with 4.6-star ratings'
    }
  ];

  return (
    <div data-testid="about-page" style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
      <SEO />
      {/* Hero Section */}
      <section
        className="py-20"
        style={{
          background: 'linear-gradient(to bottom right, #DCFCE7, #FFFFFF)',
          borderBottom: '1px solid var(--neutral-border)'
        }}
      >
        <div className="container mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            About Petsmart Puppies
          </h1>
          <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-muted)' }}>
            Your trusted pet store and puppy breeder in Noida, dedicated to bringing joy and companionship to families through healthy, happy puppies
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1761419100076-ed2be3688bc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHBlcnNvbiUyMHdpdGglMjBkb2clMjBwYXJrJTIwY2FuZGlkfGVufDB8fHx8MTc3Mjg3MDEyNXww&ixlib=rb-4.1.0&q=85"
                alt="Petsmart Puppies Store"
                className="shadow-soft"
                style={{ borderRadius: '2rem', width: '100%' }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--text-main)' }}>
                Our Story
              </h2>
              <div className="space-y-4" style={{ color: 'var(--text-muted)', lineHeight: '1.8' }}>
                <p>
                  Petsmart Puppies was founded with a simple mission: to connect loving families with healthy, happy puppies in Noida. Located in the heart of Sector 31, we have become the trusted destination for pet lovers across Delhi NCR.
                </p>
                <p>
                  With years of experience in ethical breeding and pet care, we ensure every puppy is raised in a loving environment with proper nutrition, socialization, and veterinary care. Our commitment to quality and transparency has earned us a 4.6-star rating from over 199 satisfied customers.
                </p>
                <p>
                  Beyond puppies, we offer a comprehensive range of premium pet products, expert guidance on pet care, and ongoing support to help your furry friend thrive. We're not just a pet store – we're your partners in pet parenthood.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16" style={{ backgroundColor: 'var(--neutral-bg)' }}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
              Our Core Values
            </h2>
            <p style={{ color: 'var(--text-muted)' }}>What makes Petsmart Puppies special</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center shadow-soft"
                  style={{
                    backgroundColor: 'white',
                    padding: '2rem',
                    borderRadius: '1.5rem'
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center mb-4"
                    style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: 'var(--primary-light)',
                      borderRadius: '50%'
                    }}
                  >
                    <Icon size={40} style={{ color: 'var(--primary-main)' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                    {value.title}
                  </h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: 'var(--text-main)' }}>
              Visit Our Store
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div
                className="p-6 rounded-2xl"
                style={{ backgroundColor: 'var(--primary-light)' }}
              >
                <div className="flex items-start gap-3 mb-4">
                  <MapPin size={24} style={{ color: 'var(--primary-main)' }} />
                  <div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                      Location
                    </h3>
                    <p style={{ color: 'var(--text-muted)' }}>
                      Shop No:- D1 Ground Floor<br />
                      Near DR Bhimrao Ambedkar Multispeciality Hospital<br />
                      Indergarhi, Sector 31<br />
                      Noida, Uttar Pradesh 201303
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="p-6 rounded-2xl"
                style={{ backgroundColor: 'var(--accent-light)' }}
              >
                <div className="flex items-start gap-3 mb-4">
                  <Clock size={24} style={{ color: 'var(--accent-main)' }} />
                  <div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                      Store Hours
                    </h3>
                    <p style={{ color: 'var(--text-muted)' }}>
                      Open Daily<br />
                      Closes at 10:00 PM<br />
                      <br />
                      <span className="font-semibold" style={{ color: 'var(--accent-main)' }}>
                        Walk-ins Welcome!
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;